import React from "react";
import { View, StyleSheet, Image } from "react-native";
import { Spacer } from "../../components/spacer/spacer";
import {
  Avatar,
  useTheme,
  Headline,
  Card,
  Title,
  Paragraph,
  ActivityIndicator,
  Divider,
} from "react-native-paper";
import { toHuman } from "../../utils/money";
import {
  DomusoProperty,
  DomusoPropertyProfile,
  DomusoPropertyFeeProfile,
} from "../../../types/db-types";
import { ThemeType, theme } from "../../theme/theme";
import { deriveUnitTitle, getPropertyAddressFromPropertyProfile } from "../../utils";
import { Text } from "../text/text";
import { Button, ButtonVariant } from "../button/button";
import { Inline } from "../layout/inline";
import { Stack } from "../layout/stack";

interface SingleReservation {
  accounting_details: any;
  property_unit: string;
  prop_name: string;
  prop_id: string;
  balance_due: string;
  allowed_payments: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  accounting_id: string;
  yardi_id: string;
  id: string;
  is_blacklisted: boolean;
  is_blocked: boolean;
}
export interface ActiveAutoPay {
  schedule_date: any;
  id: any;
  reservation_id: any;
}
interface ServiceCardProps extends SingleReservation {
  onPress: any;
  navigation: any;
  property: DomusoProperty;
  account_number: string;
  property_profile: DomusoPropertyProfile;
  property_fee_profile: DomusoPropertyFeeProfile;
  active_auto_pay: ActiveAutoPay;
}
export const ServiceCard = (cardProps: ServiceCardProps) => {
  const {
    accounting_details = {},
    account_number,
    active_auto_pay,
    allowed_payments,
    balance_due,
    id,
    is_blacklisted,
    is_blocked,
    navigation,
    prop_name,
    property,
    property_fee_profile,
    property_profile,
    property_unit,
  } = cardProps;
  const theme = useTheme() as ThemeType;
  const { colors, spacing } = theme;
  const styles = StyleSheet.create({
    cardContainer: {
      width: "100%",
      paddingBottom: spacing.medium,
      backgroundColor: colors.white,
      shadowColor: colors.grey,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.9,
      shadowRadius: 2,
      elevation: 1,
    },
    titleContainer: {
      backgroundColor: theme.colors.dim,
      padding: spacing.medium,
    },
    titleStyle: {
      fontSize: theme.fontSize.large,
      fontWeight: "bold",
    },
    subtitleStyle: {
      color: theme.colors.text,
      fontSize: theme.fontSize.large,
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexDirection: "row",
    },
    imageWidth: {
      height: 20,
      width: 19.67,
      resizeMode: "contain",
    },
    balanceDueText: { textAlign: "left", fontWeight: "600", color: theme.colors.darkBg },
    balanceDueDescription: { textAlign: "left", fontWeight: "600", fontSize: theme.fontSize.small },
    buttonsContainer: { display: "flex", flexDirection: "row", justifyContent: "space-around", alignItems: "flex-start" },
    button: { width: "48%" },
    buttonText: { fontSize: theme.fontSize.medium },
    iconText: {
      textDecorationLine: "underline",
    },
  });
  const title = `${deriveUnitTitle(property_unit)} - ${prop_name}`;
  return (
    <View style={styles.cardContainer}>
      <Stack space="tiny" style={styles.titleContainer} useThemeBackgroundColor={false}>
        <Text style={styles.titleStyle}>{title}</Text>
        <Text style={styles.subtitleStyle}>{getPropertyAddressFromPropertyProfile(property_profile)}</Text>
      </Stack>
      <Spacer size="medium" />
      <View>
        <View style={styles.buttonsContainer}>
          <Button
            tx="serviceStack.requestService"
            textStyle={styles.buttonText}
            style={styles.button}
            disabled={is_blacklisted}
            mode="contained"
            onPress={() =>
              navigation.navigate("CreateServiceRequest", {
                ...cardProps,
                account_number,
                accountingDetails: accounting_details,
                allowedPayments: allowed_payments,
                balanceDue: balance_due,
                propertyUnit: property_unit,
                propName: prop_name,
                reservationId: id,
                currentProperty: property,
                currentPropertyProfile: property_profile,
                currentPropertyFeeProfile: property_fee_profile,
                activeAutoPay: active_auto_pay,
                isBlacklisted: is_blacklisted,
                isBlocked: is_blocked
              })
            }
          />
          <Button
            tx="serviceStack.serviceHistory"
            mode="contained"
            variant={ButtonVariant.LIGHT}
            textStyle={styles.buttonText}
            style={styles.button}
            onPress={() =>
              navigation.navigate("ServiceRequestSpecificReservation", {
                ...cardProps,
                account_number,
                accountingDetails: accounting_details,
                allowedPayments: allowed_payments,
                balanceDue: balance_due,
                propertyUnit: property_unit,
                propName: prop_name,
                reservationId: id,
                currentProperty: property,
                currentPropertyProfile: property_profile,
                currentPropertyFeeProfile: property_fee_profile,
                activeAutoPay: active_auto_pay,
                isBlacklisted: is_blacklisted,
                isBlocked: is_blocked,
              })
            }
          />
        </View>
      </View>
    </View>
  );
};
